import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, Sparkles, User } from "lucide-react";
import { scrollToSection } from "@/lib/scrollToSection";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_koko-showcase/artifacts/qadunrd3_1000069989-removebg-preview%20%281%29.png";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);

    const checkAuth = () => {
      setIsLoggedIn(!!localStorage.getItem("koko_token"));
      setIsClientLoggedIn(!!localStorage.getItem("koko_client_token"));
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);
    window.addEventListener("focus", checkAuth);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("focus", checkAuth);
    };
  }, []);

  // Lock body scroll while mobile menu is open (prevents background jitter on iOS)
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => document.body.classList.remove("no-scroll");
  }, [mobileOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const scrollTo = (href) => {
    setMobileOpen(false);
    // If we're not on the home page, navigate there first with the hash,
    // then scroll after the landing page mounts.
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
      // HomePage will read location.state on mount; also fire a delayed scroll
      // as a safety net in case state doesn't propagate.
      setTimeout(() => scrollToSection(href), 400);
      return;
    }
    // Same page: wait one frame so the body scroll-lock releases before scrolling.
    requestAnimationFrame(() => scrollToSection(href));
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 sm:h-20 gap-4">
            <Link to="/" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="flex items-center shrink-0 tap" data-testid="nav-logo">
              <img src={LOGO_URL} alt="KoKo Sites - Web Design Studio India" className="h-12 sm:h-16 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center gap-4 ml-auto">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className="tap link-underline text-sm font-bold uppercase tracking-[0.15em] text-zinc-400 hover:text-white transition-colors duration-200 px-2"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/blog"
                data-testid="nav-blog"
                className="tap link-underline text-sm font-bold uppercase tracking-[0.15em] text-zinc-400 hover:text-white transition-colors duration-200 px-2"
              >
                Blog
              </Link>
              <div className="w-px h-5 bg-white/15 mx-2" />
              {isLoggedIn && (
                <Link
                  to="/admin"
                  data-testid="nav-dashboard"
                  className="tap inline-flex items-center justify-center gap-1.5 h-10 text-[11px] font-bold uppercase tracking-[0.15em] text-red-500 hover:text-white border-2 border-red-600/60 hover:border-red-600 hover:bg-red-600/10 px-4 transition-colors duration-200 whitespace-nowrap"
                >
                  <LayoutDashboard size={13} /> Dashboard
                </Link>
              )}
              {isClientLoggedIn ? (
                <Link
                  to="/dashboard"
                  data-testid="nav-client-dashboard"
                  className="tap inline-flex items-center justify-center gap-1.5 h-10 text-[11px] font-bold uppercase tracking-[0.15em] text-white border-2 border-white/30 hover:border-white px-4 transition-colors duration-200 whitespace-nowrap"
                >
                  <User size={13} /> My Orders
                </Link>
              ) : !isLoggedIn && (
                <Link
                  to="/login"
                  data-testid="nav-client-login"
                  className="tap inline-flex items-center justify-center gap-1.5 h-10 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300 hover:text-white border-2 border-transparent hover:border-white/20 px-3 transition-colors duration-200 whitespace-nowrap"
                >
                  <User size={13} /> Sign In
                </Link>
              )}
              <button
                onClick={() => window.dispatchEvent(new Event("koko-open-wizard"))}
                data-testid="nav-get-started"
                className="btn-brutal btn-shine inline-flex items-center justify-center gap-2 h-10 bg-red-600 text-white font-bold uppercase tracking-widest px-4 border-2 border-white hover:bg-white hover:text-black text-[11px] whitespace-nowrap"
              >
                <Sparkles size={12} />
                Get Started
              </button>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              className="lg:hidden tap touch-target inline-flex items-center justify-center text-white -mr-2 rounded-sm active:bg-white/5"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={26} strokeWidth={2.5} /> : <Menu size={26} strokeWidth={2.5} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            id="mobile-nav-panel"
            className="lg:hidden bg-[#0A0A0A] border-t border-white/10 animate-fadeIn relative z-50"
          >
            <div className="px-4 py-6 space-y-1 pb-safe">
              {navLinks.map((link, i) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  className="tap block w-full text-left text-base font-bold uppercase tracking-widest text-zinc-300 hover:text-red-500 active:text-red-500 active:bg-white/5 transition-colors px-2 py-4 border-b border-white/5 animate-fadeInUp opacity-0"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/blog"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-blog"
                style={{ animationDelay: `${navLinks.length * 60}ms` }}
                className="tap block w-full text-left text-base font-bold uppercase tracking-widest text-zinc-300 hover:text-red-500 active:text-red-500 active:bg-white/5 transition-colors px-2 py-4 border-b border-white/5 animate-fadeInUp opacity-0"
              >
                Blog
              </Link>
              {isLoggedIn && (
                <Link
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className="tap mt-3 flex items-center gap-2 w-full text-base font-bold uppercase tracking-widest text-red-500 hover:text-white active:bg-red-600/10 border border-red-600/40 hover:border-red-600 px-4 py-4 transition-colors"
                >
                  <LayoutDashboard size={18} /> Dashboard
                </Link>
              )}
              {isClientLoggedIn ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-client-dashboard"
                  className="tap mt-3 flex items-center gap-2 w-full text-base font-bold uppercase tracking-widest text-white border border-white/30 hover:border-white px-4 py-4 transition-colors"
                >
                  <User size={18} /> My Orders
                </Link>
              ) : !isLoggedIn && (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-client-login"
                  className="tap mt-3 flex items-center gap-2 w-full text-base font-bold uppercase tracking-widest text-zinc-300 hover:text-white border border-white/10 hover:border-white/30 px-4 py-4 transition-colors"
                >
                  <User size={18} /> Sign In
                </Link>
              )}
              <button
                onClick={() => { setMobileOpen(false); window.dispatchEvent(new Event("koko-open-wizard")); }}
                data-testid="mobile-nav-get-started"
                className="btn-brutal btn-shine mt-4 w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-4 border-2 border-white text-sm flex items-center justify-center gap-2"
              >
                <Sparkles size={16} />
                Get Started — Free Quote
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop (tap to close) — below menu (z-40), above content */}
      {mobileOpen && (
        <div
          className="menu-backdrop lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}

